import React, { useEffect, useState } from "react";
import { react2Angular } from "~/Core/React2Angular";
import { useOptionsContext } from "~/Contexts/OptionsContext";
import Cookies from "js-cookie";
import { useSingleSpaParcel } from "~/Hooks/SingleSpaParcel";
import styled from "styled-components";

const OPAL_CHATBOX_DOCK_STATE_CHANGED = "opal::chatbox::dockStateChanged";
const OPAL_CHATBOX_CLOSED = "opal::chatbox::closed";

const ChatButtonContainer = styled.button`
    position: fixed;
    top: 0px;
    right: 120px;
    background-color: transparent;
    border: none;
    cursor: pointer;
    z-index: 9999999;
    display: flex;
    align-items: center;
    padding: 7px 16px;
    background-color: #fff;
    border: 1px solid #d9d9d9;
    font-family: "Optimizely Sans", sans-serif;

    &:hover {
        background-color: #f5f5f5;
    }
`;

const AvatarWrapper = styled.div`
    width: 24px;
    height: 24px;
    margin-right: 8px;
    border-radius: 50%;
    overflow: hidden;
`;

const AvatarImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ButtonLabel = styled.span`
    font-size: 14px;
    font-weight: 500;
`;

const ChatParcelContainer = styled.div<{ dockState: string }>`
    position: fixed !important;
    top: 41px !important;
    right: 20px !important;
    left: auto !important;
    width: auto !important;
    height: auto !important;
    border-radius: 8px !important;
    border-left: none !important;
    z-index: 9999999 !important;

    /* Minimal CSS - let JavaScript handle most styling */
    .react-draggable {
        transition: all 0.3s ease;
    }
`;

export const OpalChat = () => {
    const options = useOptionsContext();
    const [isChatVisible, setIsChatVisible] = useState(false);
    const [dockState, setDockState] = useState("popout");

    const applyDockStyles = (state: string) => {
        setTimeout(() => {
            const container = document.querySelector('[data-chat-container="true"]');

            // Also handle tooltips that might be rendered at the document level
            const tooltipSelectors = [
                '[role="tooltip"]',
                "[data-radix-popper-content-wrapper]",
                "[data-side]",
                'div[id^="radix-"]',
                ".tooltip",
                '[class*="tooltip"]',
            ];
            tooltipSelectors.forEach(selector => {
                const tooltips = document.querySelectorAll(selector);
                tooltips.forEach((tooltip: any) => {
                    const tooltipStyle = tooltip.style;
                    if (tooltipStyle) {
                        tooltipStyle.setProperty("z-index", "9999999", "important");
                    }
                });
            });

            if (container) {
                const chatWidget = container.querySelector("div");

                if (chatWidget) {
                    const element = chatWidget as HTMLElement;
                    const elementStyle = element.style;

                    if (state === "docked") {
                        elementStyle.setProperty("top", "41px", "important");
                        elementStyle.setProperty("height", "calc(100vh - 41px)", "important");
                    } else if (state === "fullscreen") {
                        // For fullscreen, the chat creates a new div at the end of the body
                        const bodyChildren = document.body.children;
                        const lastDiv = bodyChildren[bodyChildren.length - 1] as HTMLElement;

                        if (lastDiv && lastDiv.tagName === "DIV") {
                            const lastDivStyle = lastDiv.style;
                            lastDivStyle.setProperty("top", "41px", "important");
                            lastDivStyle.setProperty("height", "calc(100vh - 41px)", "important");
                        }
                    }
                }
            }
        }, 50);
    };

    useEffect(() => {
        const closeEventListener = () => {
            setIsChatVisible(false);
        };

        const dockStateChangedListener = (event: CustomEvent) => {
            const { state } = event.detail || {};
            setDockState(state);
            applyDockStyles(state);
        };

        // Set up a MutationObserver to catch dynamically added tooltips
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    if (node.nodeType === 1) {
                        const element = node as HTMLElement;

                        const isTooltip =
                            element.getAttribute("role") === "tooltip" ||
                            element.hasAttribute("data-radix-popper-content-wrapper") ||
                            element.hasAttribute("data-side") ||
                            element.id?.startsWith("radix-") ||
                            element.className?.includes("tooltip");
                        if (isTooltip) {
                            element.style.setProperty("z-index", "9999999", "important");
                        }

                        const tooltipChildren = element.querySelectorAll(
                            '[role="tooltip"], [data-radix-popper-content-wrapper], [data-side], div[id^="radix-"], .tooltip, [class*="tooltip"]',
                        );
                        tooltipChildren.forEach((tooltip: any) => {
                            tooltip.style.setProperty("z-index", "9999999", "important");
                        });
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        window.addEventListener(OPAL_CHATBOX_CLOSED, closeEventListener);
        window.addEventListener(OPAL_CHATBOX_DOCK_STATE_CHANGED, dockStateChangedListener as EventListener);

        return () => {
            observer.disconnect();
            window.removeEventListener(OPAL_CHATBOX_CLOSED, closeEventListener);
            window.removeEventListener(OPAL_CHATBOX_DOCK_STATE_CHANGED, dockStateChangedListener as EventListener);
        };
    });

    useEffect(() => {
        if (isChatVisible) {
            applyDockStyles(dockState);
        }
    }, [dockState, isChatVisible]);

    const parcelConfig: any = {
        turnstileInstanceId: options.turnstile.instanceId,
        storeKey: "cfg-admin",
        productScope: "CFG",
        subProductName: "Configured Commerce",
        accessToken: Cookies.get("opti_id_token"),
    };

    // if the projectIdentifier is not set or it's a qa site, use the development base URL, otherwise it defaults to prod
    if (!options.environment.projectIdentifier || window.location.hostname.endsWith("insitesoftqa.com")) {
        parcelConfig.baseUrl = "https://inte.opal-backend.optimizely.com";
    }

    const parcel = useSingleSpaParcel("@optimizely/opal-chat", parcelConfig);

    const toggleChat = () => {
        setIsChatVisible(!isChatVisible);
    };

    return (
        <>
            <ChatButtonContainer
                type="button"
                aria-label="Ask Opal"
                aria-pressed={isChatVisible}
                className="ndl-Button ndl-Button--secondaryAlt ndl-Button--medium"
                onClick={toggleChat}
            >
                <AvatarWrapper className="ndl-Avatar ndl-Avatar--xsmall _6xp7r6 _1x7odu0">
                    <div className="ndl-Avatar-wrapper">
                        <AvatarImage
                            aria-label="avatar"
                            className="ndl-Avatar-image"
                            src="https://cdn.cmp.optimizely.com/static/images/opal.png?version=02e407c2ab974cb6dbd2"
                        />
                    </div>
                </AvatarWrapper>
                <ButtonLabel className="ndl-Button-label">Ask Opal</ButtonLabel>
            </ChatButtonContainer>
            {isChatVisible && parcel && (
                <ChatParcelContainer dockState={dockState} data-chat-container="true">
                    {parcel}
                </ChatParcelContainer>
            )}
        </>
    );
};

angular.module("insite-admin").component("opalChat", react2Angular(OpalChat));
